import { queryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { fetchCertificationStandards } from '@/api/certifications';
import {
  fetchAllCompaniesDropdown,
  fetchCollaborationStatuses,
  fetchNetworkCompaniesDropdown,
  fetchParentStyleOptions,
  fetchParentVariantOptions,
  fetchProductCategories,
  fetchRelationshipTypes,
  fetchStyleTypeListPage,
  fetchUsersByCompanyIds,
} from '@/api/dropdowns';
import type { CompanyOption } from '@/api/my-styles/types';
import type { FilterOption } from '@/api/orders/types';
import { useGroupedFacilityProcessOptions } from '@/hooks/useGroupedFacilityProcessOptions';
import { companyRefIds } from '@/lib/utils/companyRefs';
import { logoUrl } from '@/lib/utils/logoUrl';

const STALE_REFERENCE = 24 * 60 * 60 * 1000;
const STALE_DYNAMIC = 5 * 60 * 1000;

/**
 * Flat variant of useGroupedFacilityProcessOptions() for callers that don't
 * need the group structure. Shares the same query (and cache entry) as the
 * grouped hook — flattening happens here, not via a second network request.
 */
export function useFacilityProcessOptions() {
  const query = useGroupedFacilityProcessOptions();
  const options: FilterOption[] = (query.data ?? []).flatMap(g =>
    g.options.map(o => ({ key: o.key, label: o.label })),
  );
  return { ...query, data: options };
}

export function useCollaborationStatusOptions(enabled = true) {
  return useQuery({
    queryKey: ['options', 'collaboration-statuses'],
    queryFn: async () => {
      const res = await fetchCollaborationStatuses();
      return (res?.data ?? []).map(s => ({ key: s.id, label: s.name }));
    },
    staleTime: STALE_REFERENCE,
    enabled,
  });
}

export function useRelationshipTypeOptions(enabled = true) {
  return useQuery({
    queryKey: ['options', 'relationship-types'],
    queryFn: async () => {
      const res = await fetchRelationshipTypes();
      return (res?.data ?? []).map(r => ({ key: r.id, label: r.name }));
    },
    staleTime: STALE_REFERENCE,
    enabled,
  });
}

export function networkCompanyOptionsQueryOptions() {
  return queryOptions({
    queryKey: ['options', 'companies-dropdown'] as const,
    queryFn: async () => {
      const res = await fetchNetworkCompaniesDropdown();
      return (res?.data ?? []).map(c => ({
        key: c.id,
        label: c.displayName || c.name || c.officialName,
        country: c.country ?? null,
        officialName: c.officialName ?? null,
        agentIds: companyRefIds(c.agents),
        vendorIds: companyRefIds(c.vendors),
      }));
    },
    staleTime: STALE_DYNAMIC,
  });
}

export function useNetworkCompanyOptions(opts?: { enabled?: boolean }) {
  // Gated so a caller that supplies its own company list — management, which
  // picks from every company rather than one network — fetches nothing.
  return useQuery({
    ...networkCompanyOptionsQueryOptions(),
    ...(opts?.enabled === undefined ? {} : { enabled: opts.enabled }),
  });
}

/**
 * Distinct official names as filter options, sorted alphabetically.
 *
 * The option `key` is the official name itself, not the company id, because
 * the network filter sends the selected names to the companies API as an
 * exact-match array on `officialName`.
 */
export function toOfficialNameOptions(
  companies: { officialName?: string | null }[],
) {
  const seen = new Set<string>();
  const options: { key: string; label: string }[] = [];
  for (const c of companies) {
    const name = c.officialName?.trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    options.push({ key: name, label: name });
  }
  return options.sort((a, b) => a.label.localeCompare(b.label));
}

/** Official names of the network companies. Shares the cache entry of
 *  {@link networkCompanyOptionsQueryOptions} via `select` — no extra request. */
export function useNetworkOfficialNameOptions() {
  return useQuery({
    ...networkCompanyOptionsQueryOptions(),
    select: toOfficialNameOptions,
  });
}

export function allCompanyOptionsQueryOptions() {
  return queryOptions({
    queryKey: ['options', 'all-companies-dropdown'] as const,
    queryFn: async () => {
      const res = await fetchAllCompaniesDropdown();
      return (res?.data ?? []).map(c => ({
        key: c.id,
        label: c.displayName || c.name || c.officialName,
        officialName: c.officialName ?? null,
        logo: logoUrl({
          featuredImageFileUrl: c.featuredImageFileUrl ?? null,
          imageList: c.imageList ?? null,
        }),
        activeUsersCount: c.activeUsersCount ?? 0,
        totalUsersCount: c.totalUsersCount ?? 0,
        agentIds: companyRefIds(c.agents),
        vendorIds: companyRefIds(c.vendors),
      }));
    },
    staleTime: STALE_DYNAMIC,
  });
}

export interface CompanyMeta {
  logo: string | null;
  activeUsersCount: number;
  totalUsersCount: number;
}

/**
 * `companyId → logo + user counts` for lists whose own endpoint returns
 * neither. Shares the cache entry of {@link allCompanyOptionsQueryOptions}, so
 * a screen already using the dropdown pays no extra request.
 */
export function useCompanyMetaMap(enabled = true) {
  return useQuery({
    ...allCompanyOptionsQueryOptions(),
    enabled,
    select: companies => {
      const map: Record<string, CompanyMeta> = {};
      for (const c of companies) {
        map[c.key] = {
          logo: c.logo,
          activeUsersCount: c.activeUsersCount,
          totalUsersCount: c.totalUsersCount,
        };
      }
      return map;
    },
  });
}

export function useAllCompanyOptions(enabled = true) {
  return useQuery({ ...allCompanyOptionsQueryOptions(), enabled });
}

/** Official names across all companies (own company included), for dialogs
 *  whose list is fetched with `includeOwnCompany=true`. Shares the cache entry
 *  of {@link allCompanyOptionsQueryOptions}. */
export function useAllCompanyOfficialNameOptions(enabled = true) {
  return useQuery({
    ...allCompanyOptionsQueryOptions(),
    enabled,
    select: toOfficialNameOptions,
  });
}

/**
 * Users of `companyId` that can be set as responsible person.
 *
 * Scoped by `user_companies.company_id`, which is exactly the set the backend
 * validates the `responsible_person` data point against on write — so every
 * offered option is accepted by the PATCH. Do NOT go back to the coworker
 * endpoint (`selectCoWorkers=true`): that one filters on
 * `users.primary_company_id` and silently drops users whose primary company is
 * elsewhere, which empties the dropdown entirely (DELTA-2332).
 */
export function useResponsiblePersonOptions(
  companyId?: string,
  enabled = true,
) {
  return useUsersByCompanyIds(companyId ?? '', enabled);
}

export function useUsersByCompanyIds(companyIds: string, enabled = true) {
  return useQuery({
    queryKey: ['options', 'users-by-company-ids', companyIds],
    queryFn: async () => {
      const users = await fetchUsersByCompanyIds(companyIds);
      const seen = new Set<string>();
      return (users ?? [])
        .filter(u => {
          const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.toLowerCase();
          return !name.includes('anonymized') && !/\*/.test(name);
        })
        .filter(u => {
          const label = `${u.firstName ?? ''} ${u.lastName ?? ''}`
            .trim()
            .toLowerCase();
          if (seen.has(label)) return false;
          seen.add(label);
          return true;
        })
        .map(u => ({
          key: u.id,
          label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim(),
        }));
    },
    enabled: enabled && !!companyIds,
    staleTime: STALE_DYNAMIC,
  });
}

export function useCertificationStandardOptions() {
  return useQuery({
    queryKey: ['options', 'certification-standards'],
    queryFn: async () => {
      const res = await fetchCertificationStandards();
      return (res?.data ?? []).filter(
        s => s.type !== 'COMPANY_DOCUMENT' && s.type !== 'AUDIT',
      );
    },
    staleTime: STALE_REFERENCE,
  });
}

export function styleTypeOptionsQueryOptions() {
  return queryOptions({
    queryKey: ['options', 'style-types'] as const,
    queryFn: async () => {
      const LIMIT = 200;
      const first = await fetchStyleTypeListPage({ page: 1, limit: LIMIT });
      const totalPages = first?.metadata?.pagination?.totalPages ?? 1;
      let results = first?.data ?? [];
      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            fetchStyleTypeListPage({ page: i + 2, limit: LIMIT }),
          ),
        );
        results = [...results, ...rest.flatMap(r => r?.data ?? [])];
      }
      return results.map(s => ({ key: s.id, label: s.name }));
    },
    staleTime: STALE_REFERENCE,
  });
}

export function useStyleTypeOptions(enabled = true) {
  return useQuery({ ...styleTypeOptionsQueryOptions(), enabled });
}

export function useParentStyleOptions(companyId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['options', 'parent-styles', companyId],
    queryFn: async () => {
      const res = await fetchParentStyleOptions({
        page: 1,
        limit: 200,
        companyId,
        isArchived: false,
      });
      return (res?.data ?? []).map(s => ({ key: s.id, label: s.name }));
    },
    enabled: enabled && !!companyId,
    staleTime: STALE_DYNAMIC,
  });
}

export function useParentVariantOptions(companyId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['options', 'parent-variants', companyId],
    queryFn: async () => {
      const res = await fetchParentVariantOptions({
        page: 1,
        limit: 200,
        companyId,
        isArchived: false,
      });
      return (res?.data ?? []).map(v => ({ key: v.id, label: v.name }));
    },
    enabled: enabled && !!companyId,
    staleTime: STALE_DYNAMIC,
  });
}

export function useNetworkCompanyMap(): Map<string, CompanyOption> {
  const { data } = useQuery({
    queryKey: ['options', 'companies-dropdown-with-country'],
    queryFn: async () => {
      const res = await fetchNetworkCompaniesDropdown();
      return (res?.data ?? []).map(c => ({
        key: c.id,
        label: c.officialName || c.name,
        country: c.country ?? null,
      }));
    },
    staleTime: STALE_DYNAMIC,
  });
  return useMemo(() => new Map((data ?? []).map(c => [c.key, c])), [data]);
}

export function useProductCategoryOptions() {
  const { data: categories } = useQuery({
    queryKey: ['options', 'product-categories'],
    queryFn: async () => {
      const res = await fetchProductCategories();
      return res?.data ?? [];
    },
    staleTime: STALE_REFERENCE,
  });

  const productLevels = (categories ?? [])
    .filter(c => c.parentId === null)
    .map(c => ({ key: c.id, name: c.name }));

  const productTypes = (categories ?? [])
    .filter(c => c.parentId !== null)
    .map(c => ({ key: c.id, name: c.name }));

  return { productLevels, productTypes };
}
